import React from 'react';

import { Button, Flex, Modal, Stack, Text, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';

import { ExperimentResponse } from '../generated/generatedOpenAPI';
import { useDeleteExperiment } from '../store/store';

interface DeleteExperimentModalProps {
  opened: boolean;
  close: () => void;
  experiment: ExperimentResponse;
}

export function DeleteExperimentModal({ opened, close, experiment }: DeleteExperimentModalProps) {
  const theme = useMantineTheme();

  const [deleteExperiment, { isLoading: isDeletingExperiment }] = useDeleteExperiment();

  const handleDeleteExperiment = React.useCallback(async () => {
    try {
      if (!experiment.id) {
        throw new Error('Experiment ID is required');
      }
      await deleteExperiment({
        id: experiment.id,
      }).unwrap();
      showNotification({
        title: 'Experiment Deleted',
        message: 'Experiment has been deleted successfully',
        color: theme.colors.green[7],
        autoClose: 2000,
      });
      close();
    } catch (error) {
      let errorMessage;
      if (error instanceof Error && 'data' in error && 'detail' in (error as any).data) {
        errorMessage = `Experiment could not be deleted: ${(error as any).data.detail}`;
      } else {
        errorMessage = 'Experiment could not be deleted';
      }
      showNotification({
        title: 'Failed to delete experiment',
        message: errorMessage,
        color: theme.colors.red[7],
        autoClose: 2000,
      });
    }
  }, [experiment.id, deleteExperiment, theme.colors.green, theme.colors.red, close]);

  return (
    <Modal
      data-testid="delete-experiment-confirmation-modal"
      opened={opened}
      onClose={close}
      title="Confirm Deletion of experiment"
      closeOnClickOutside={false}
      size="md"
    >
      <Stack gap="md" h="100%" justify="space-between">
        <Text size="md">
          Are you sure you want to delete the experiment with the name
          <Text fw={500} display="inline" pl={3}>
            {experiment.title}
          </Text>
          ? this action is irreversible.
        </Text>

        <Flex gap="md" justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button data-testid="delete-experiment-confirm-button" variant="filled" color="red.7" onClick={handleDeleteExperiment} loading={isDeletingExperiment}>
            Delete
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
}
